<template>
  <div class="upload-section">
    <div class="upload-container">
      <div class="upload-icon">
        <i class="fa fa-magic text-primary" style="font-size: 3rem;"></i>
      </div>
      <h4 class="upload-title">Análisis Inteligente de Documentos</h4>
      <p class="upload-description">
        <i class="fa fa-info-circle" v-b-tooltip.hover title="Requisitos del archivo (producción):
          • Formato: PDF únicamente
          • Tamaño máximo: 20MB
          • El documento debe contener texto legible (no solo imágenes escaneadas)
          • No debe estar protegido con contraseña"></i>
        Nuestra IA analizará automáticamente tu documento PDF y extraerá toda la información relevante del producto.
      </p>

      <div class="upload-area" @click="handleClick" @drop="handleDrop" @dragover="handleDragOver"
        @dragenter="handleDragEnter" @dragleave="handleDragLeave" :class="{ 'drag-over': isDragOver }" v-b-tooltip.hover
        title="Arrastra y suelta o haz clic para seleccionar">
        <div class="upload-content">
          <i class="fa fa-cloud-upload text-muted" style="font-size: 2.5rem;"></i>
          <h5 class="mt-3">Sube tu documento PDF</h5>
          <p class="text-muted">Arrastra y suelta o haz clic para seleccionar</p>
          <b-button variant="outline-primary" size="lg" class="mt-3">
            <i class="fa fa-file-pdf-o mr-2"></i>
            Seleccionar PDF
          </b-button>
        </div>
      </div>

      <input 
        ref="fileInput" 
        type="file" 
        accept=".pdf" 
        @change="onFileSelected" 
        class="d-none"
      />

      <!-- Validación de archivo -->
      <div v-if="fileValidation" class="file-validation mt-3">
        <div v-if="fileValidation.isValid" class="validation-success">
          <i class="fa fa-check-circle"></i>
          <span>{{ fileValidation.message }}</span>
        </div>
        <div v-else class="validation-error">
          <i class="fa fa-exclamation-triangle"></i>
          <span>{{ fileValidation.message }}</span>
        </div>
      </div>

      <div class="upload-features mt-4">
        <div class="feature-item">
          <i class="fa fa-magic text-success"></i>
          <span>Extracción automática de datos</span>
        </div>
        <div class="feature-item">
          <i class="fa fa-shield text-info"></i>
          <span>Análisis seguro y confidencial</span>
        </div>
        <div class="feature-item">
          <i class="fa fa-clock-o text-warning"></i>
          <span>Procesamiento en segundos</span>
        </div>
      </div>

      <!-- Opción para crear sin FDS -->
      <div v-if="canShowManualCreationButton" class="manual-creation-section mt-4">
        <b-button variant="outline-secondary" size="lg"  @click="createWithoutFDS">
          <i class="fa fa-file-text-o mr-2"></i>
          CREAR SIN FDS
        </b-button>
        
        <small class="text-muted d-block mt-3 text-center px-3">
          <i class="fa fa-info-circle mr-1"></i>
          Use esta opción solo si el producto no cuenta con FDS disponible o el formato no es admisible.
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'FileUploadArea',
  
  props: {
    opcionGuardar: {
      type: Boolean,
      required: false,
      default: false
    },
    crearProductos: {
      type: Boolean,
      required: false,
      default: false
    },
    hasExistingCache: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  
  data() {
    return {
      file: null,
      isDragOver: false,
      fileValidation: null
    }
  },
  
  computed: {
    ...mapState('autenticacion', ['usuarioEmpresaContratista', 'usuarioAdmin']),
    
    /**
     * Valida si el botón de creación manual debe mostrarse
     */
    canShowManualCreationButton() {
      if (this.usuarioAdmin && this.opcionGuardar && this.crearProductos) {
        return true;
      }
      if (this.usuarioEmpresaContratista && this.crearProductos) {
        return true;
      }
      return false;
    }
  },
  
  watch: {
    file(newFile) {
      if (newFile) {
        this.$nextTick(() => {
          this.validateFile(newFile);
        });
      } else {
        this.fileValidation = null;
      }
    }
  },
  methods: {
    handleClick() {
      // Simplemente hacer click en el input file nativo
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      } else {
        this.$alertError('No se puede acceder al campo de archivo', 2000, false, 'Asistente IA · Crear Producto', 'info');
      }
    },
    
    handleDragEnter(event) {
      event.preventDefault();
      this.isDragOver = true;
    },
    
    handleDragLeave(event) {
      event.preventDefault();
      this.isDragOver = false;
    },
    
    handleDragOver(event) {
      event.preventDefault();
    },
    
    async handleDrop(event) {
      event.preventDefault();
      this.isDragOver = false;

      const files = event.dataTransfer.files;

      if (files.length > 0) {
        const file = files[0];
        
        // Validar archivo primero
        this.validateFile(file);
        
        if (this.fileValidation && this.fileValidation.isValid) {
          // Si hay caché existente, pedir confirmación
          if (this.hasExistingCache) {
            const confirmed = await this.confirmOverwriteCache();
            if (!confirmed) {
              this.clearFile();
              return;
            }
          }
          
          this.file = file;
          this.$emit('file-selected', this.file);
        } else {
            this.clearFile();
        }
      }
    },
    
    async onFileSelected(event) {
      const selectedFile = event.target.files[0];
      
      if (selectedFile) {
        // Validar archivo primero
        this.validateFile(selectedFile);
        
        if (this.fileValidation && this.fileValidation.isValid) {
          // Si hay caché existente, pedir confirmación
          if (this.hasExistingCache) {
            const confirmed = await this.confirmOverwriteCache();
            if (!confirmed) {
              // Limpiar el input file si cancela, para permitir seleccionar el mismo archivo después
              event.target.value = '';
              this.clearFile();
              return;
            }
          }
          
          this.file = selectedFile;
          this.$emit('file-selected', this.file);
        } else {
            this.clearFile();
        }
      } else {
        this.clearFile();
        this.$alertError('No se seleccionó ningún archivo', 2000, false, 'Asistente IA · Crear Producto', 'info');
      }
    },

    /**
     * Valida el archivo seleccionado
     */
    validateFile(file) {
      this.fileValidation = null;
      
      if (!file) {
        this.$alertError('No se seleccionó ningún archivo', 2000, false, 'Asistente IA · Crear Producto', 'info');
        return;
      }

      // Validar tipo de archivo
      if (file.type !== 'application/pdf') {
        this.$alertError('El archivo debe ser un documento PDF válido.', 2000, false, 'Asistente IA · Crear Producto', 'info');
        this.fileValidation = {
          isValid: false,
          message: 'El archivo debe ser un documento PDF válido.'
        };
        return;
      }

      // Validar tamaño del archivo
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        this.$alertError('El archivo es muy grande (más de 20MB).', 2000, false, 'Asistente IA · Crear Producto', 'info');
        this.fileValidation = {
          isValid: false,
          message: `El archivo es muy grande (${(file.size / 1024 / 1024).toFixed(2)} MB). Máximo permitido: 20MB.`
        };
        return;
      }

      // Validar nombre del archivo
      if (file.name.length > 255) {
        this.$alertError('El nombre del archivo es demasiado largo.', 2000, false, 'Asistente IA · Crear Producto', 'info');
        this.fileValidation = {
          isValid: false,
          message: 'El nombre del archivo es demasiado largo.'
        };
        return;
      }

      // Archivo válido
      const validation = {
        isValid: true,
        message: `Archivo válido: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`
      };
      
      // Usar this.$set para asegurar reactividad
      this.$set(this, 'fileValidation', validation);
    },

    /**
     * Limpia el archivo seleccionado
     */
    clearFile() {
      this.file = null;
      this.isDragOver = false;
      this.fileValidation = null;
    },

    /**
     * Resetea el componente completo
     */
    reset() {
      this.clearFile();
    },

    /**
     * Crear producto sin FDS (manual)
     */
    createWithoutFDS() {
      this.$emit('create-without-fds');
    },

    /**
     * Pide confirmación al usuario para sobrescribir la caché
     */
    async confirmOverwriteCache() {
      return new Promise((resolve) => {
        this.$alertConfirmation({
          title: '¿Sobrescribir procesamiento anterior?',
          text: "Ya existe un proceso de IA pendiente. Si sube un nuevo documento, perderá los datos anteriores.",
          icon: 'warning',
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: 'Sí, sobrescribir',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
          onDismiss: () => resolve(false)
        });
      });
    }
  }
}
</script>

<style scoped>
/* Upload Section Styles */
.upload-section {
  padding: 2rem 0;
}

.upload-container {
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
}

.upload-icon {
  margin-bottom: 1.5rem;
}

.upload-title {
  color: #2c3e50;
  font-weight: 700;
  margin-bottom: 1rem;
}

.upload-description {
  color: #6c757d;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.upload-area {
  border: 3px dashed #e9ecef;
  border-radius: 15px;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
}

.upload-area:hover,
.upload-area.drag-over {
  border-color: #007bff;
  background: linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.15);
}

.upload-area.drag-over {
  border-style: solid;
  border-width: 3px;
  background: linear-gradient(135deg, #e3f2fd 0%, #d1ecf1 100%);
  box-shadow: 0 12px 35px rgba(0, 123, 255, 0.25);
}

.upload-content h5 {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.upload-features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 10px;
  font-size: 0.9rem;
}

.feature-item i {
  font-size: 1.2rem;
  width: 20px;
}

/* File Validation Styles */
.file-validation {
  padding: 1rem;
  border-radius: 8px;
  text-align: left;
}

.validation-success {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #28a745;
  background: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.2);
  padding: 0.75rem;
  border-radius: 6px;
}

.validation-error {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #dc3545;
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.2);
  padding: 0.75rem;
  border-radius: 6px;
}

.file-validation i {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.file-validation span {
  font-size: 0.9rem;
  font-weight: 500;
  word-break: break-word;
}

/* Manual Creation Section */
.manual-creation-section {
  padding: 0 1rem;
  margin-top: 30px!important;
}

.manual-creation-section .btn {
  transition: all 0.3s ease;
}

.manual-creation-section .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.2);
}

.manual-creation-section small {
  line-height: 1.5;
  font-size: 0.85rem;
}
</style>